import { useState } from 'react'
import { useDocumentRecord } from '@/hooks'
import { SectionCard } from '@/components/shared/section-card'
import { DataTable } from '@/components/shared/data-table'
import { StatusBadge } from '@/components/shared/status-badge'
import { 
  Save, 
  RefreshCw, 
  AlertCircle,
  CheckCircle2,
  Search,
  Download,
  Globe
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatDateTime } from '@/lib/format'
import { db } from '@/lib/firebase'
import { doc, setDoc, Timestamp } from 'firebase/firestore'

interface TranslationEntry {
  key: string
  en: string
  id: string
  category: string
  lastUpdated?: string
  updatedBy?: string
}

interface TranslationDoc {
  id?: string
  data: Record<string, any>
  updatedAt?: any
  version?: number
}

export function TranslationsPage() {
  const [activeLang, setActiveLang] = useState<'en' | 'id'>('en')
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState<string>('all')
  const [isEditing, setIsEditing] = useState(false)
  const [editEntry, setEditEntry] = useState<TranslationEntry | null>(null)
  const [notification, setNotification] = useState<{type: 'success' | 'error', message: string} | null>(null)

  // Fetch translations from Firestore
  const enTranslations = useDocumentRecord<TranslationDoc>(['translations', 'en'])
  const idTranslations = useDocumentRecord<TranslationDoc>(['translations', 'id'])

  // Convert nested object to flat array for display
  const flattenTranslations = (data: Record<string, any>, prefix = '', category = ''): TranslationEntry[] => {
    if (!data) return []
    
    const entries: TranslationEntry[] = []
    
    Object.entries(data).forEach(([key, value]) => {
      const fullKey = prefix ? `${prefix}.${key}` : key
      
      if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
        entries.push(...flattenTranslations(value, fullKey, category || key))
      } else {
        entries.push({
          key: fullKey,
          en: '', // Will be filled from en data
          id: '', // Will be filled from id data
          category: category || 'general',
        })
      }
    })
    
    return entries
  }

  // Get value by key path from nested object
  const getValueByKey = (obj: Record<string, any>, key: string): string => {
    const keys = key.split('.')
    let value = obj
    for (const k of keys) {
      if (value && typeof value === 'object') {
        value = value[k]
      } else {
        return ''
      }
    }
    return typeof value === 'string' ? value : JSON.stringify(value)
  }

  // Merge EN and ID translations
  const mergedTranslations: TranslationEntry[] = (() => {
    const enData = enTranslations.data?.data || {}
    const idData = idTranslations.data?.data || {}
    
    // Get all unique keys
    const allKeys = new Set<string>()
    
    const collectKeys = (obj: Record<string, any>, prefix = '') => {
      Object.entries(obj).forEach(([key, value]) => {
        const fullKey = prefix ? `${prefix}.${key}` : key
        if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
          collectKeys(value, fullKey)
        } else {
          allKeys.add(fullKey)
        }
      })
    }
    
    collectKeys(enData)
    collectKeys(idData)
    
    return Array.from(allKeys).map(key => {
      const category = key.split('.')[0]
      return {
        key,
        en: getValueByKey(enData, key),
        id: getValueByKey(idData, key),
        category,
      }
    })
  })()

  // Get unique categories
  const categories = ['all', ...Array.from(new Set(mergedTranslations.map(t => t.category)))]

  // Filter translations
  const filteredTranslations = mergedTranslations.filter(entry => {
    const matchesSearch = 
      entry.key.toLowerCase().includes(searchTerm.toLowerCase()) ||
      entry.en.toLowerCase().includes(searchTerm.toLowerCase()) ||
      entry.id.toLowerCase().includes(searchTerm.toLowerCase())
    
    const matchesCategory = selectedCategory === 'all' || entry.category === selectedCategory
    
    return matchesSearch && matchesCategory
  })

  // Statistics
  const stats = {
    total: mergedTranslations.length,
    withEn: mergedTranslations.filter(t => t.en && t.en.trim()).length,
    withId: mergedTranslations.filter(t => t.id && t.id.trim()).length,
    incomplete: mergedTranslations.filter(t => !t.en || !t.id || !t.en.trim() || !t.id.trim()).length,
  }

  const handleSave = async (entry: TranslationEntry) => {
    try {
      const enData = enTranslations.data?.data || {}
      const idData = idTranslations.data?.data || {}

      // Helper to set nested value
      const setNestedValue = (obj: Record<string, any>, key: string, value: string) => {
        const keys = key.split('.')
        let current = obj
        for (let i = 0; i < keys.length - 1; i++) {
          if (!current[keys[i]]) current[keys[i]] = {}
          current = current[keys[i]]
        }
        current[keys[keys.length - 1]] = value
      }

      setNestedValue(enData, entry.key, entry.en)
      setNestedValue(idData, entry.key, entry.id)

      // Save to Firestore
      await setDoc(doc(db, 'translations', 'en'), {
        data: enData,
        updatedAt: Timestamp.now(),
        version: Date.now(),
      })

      await setDoc(doc(db, 'translations', 'id'), {
        data: idData,
        updatedAt: Timestamp.now(),
        version: Date.now(),
      })

      setNotification({ type: 'success', message: 'Translation saved successfully!' })
      setTimeout(() => setNotification(null), 3000)
      setIsEditing(false)
      setEditEntry(null)
    } catch (error) {
      console.error('Error saving translation:', error)
      setNotification({ type: 'error', message: 'Failed to save translation' })
    }
  }

  const handleExport = () => {
    const data = {
      en: enTranslations.data?.data || {},
      id: idTranslations.data?.data || {},
      exportedAt: new Date().toISOString(),
    }
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `translations-${new Date().toISOString().split('T')[0]}.json`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="rounded-[36px] border border-orange-500/20 bg-gradient-to-br from-orange-500/10 to-amber-500/5 p-8">
        <div className="inline-flex rounded-full border border-orange-500/30 bg-orange-500/10 px-4 py-1 text-xs uppercase tracking-[0.24em] text-orange-400">
          Localization
        </div>
        <h2 className="mt-5 max-w-2xl text-4xl font-semibold leading-tight text-white">
          Translation Management
        </h2>
        <p className="mt-4 max-w-2xl text-base leading-7 text-stone-400">
          Manage app translations for English and Indonesian. Changes are synced to mobile apps in real-time.
        </p>
      </div>

      {/* Notification */}
      {notification && (
        <div className={cn(
          "rounded-2xl p-4 flex items-center gap-3",
          notification.type === 'success' ? 'bg-emerald-500/10 border border-emerald-500/30 text-emerald-400' : 'bg-rose-500/10 border border-rose-500/30 text-rose-400'
        )}>
          {notification.type === 'success' ? <CheckCircle2 className="h-5 w-5" /> : <AlertCircle className="h-5 w-5" />}
          {notification.message}
        </div>
      )}

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-4">
        <SectionCard title="Total Keys" description="All translation keys">
          <p className="text-3xl font-bold text-white">{stats.total}</p>
        </SectionCard>
        <SectionCard title="English" description="Translated to English">
          <p className="text-3xl font-bold text-blue-400">{stats.withEn}</p>
          <p className="text-sm text-stone-500">{Math.round((stats.withEn / stats.total) * 100)}% complete</p>
        </SectionCard>
        <SectionCard title="Indonesian" description="Translated to Indonesian">
          <p className="text-3xl font-bold text-emerald-400">{stats.withId}</p>
          <p className="text-sm text-stone-500">{Math.round((stats.withId / stats.total) * 100)}% complete</p>
        </SectionCard>
        <SectionCard title="Incomplete" description="Missing translations">
          <p className="text-3xl font-bold text-amber-400">{stats.incomplete}</p>
          <p className="text-sm text-stone-500">Needs attention</p>
        </SectionCard>
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-4 rounded-2xl border border-stone-800 bg-stone-900/50 p-4">
        {/* Language Toggle */}
        <div className="flex items-center gap-2 rounded-xl border border-stone-800 bg-stone-900 p-1">
          <button
            onClick={() => setActiveLang('en')}
            className={cn(
              'flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition',
              activeLang === 'en' ? 'bg-blue-500/20 text-blue-400' : 'text-stone-400 hover:text-white'
            )}
          >
            <Globe className="h-4 w-4" />
            English
          </button>
          <button
            onClick={() => setActiveLang('id')}
            className={cn(
              'flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition',
              activeLang === 'id' ? 'bg-emerald-500/20 text-emerald-400' : 'text-stone-400 hover:text-white'
            )}
          >
            <Globe className="h-4 w-4" />
            Indonesia
          </button>
        </div>

        {/* Search */}
        <div className="flex-1 min-w-[200px]">
          <div className="relative">
            <Search className="absolute left-3 top-3 h-4 w-4 text-stone-500" />
            <input
              type="text"
              placeholder="Search keys or translations..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full rounded-xl border border-stone-800 bg-stone-900 pl-10 pr-4 py-2.5 text-sm text-white focus:border-orange-500 focus:outline-none"
            />
          </div>
        </div>

        {/* Category Filter */}
        <select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          className="rounded-xl border border-stone-800 bg-stone-900 px-4 py-2.5 text-sm text-white focus:border-orange-500 focus:outline-none"
        >
          {categories.map(cat => (
            <option key={cat} value={cat}>
              {cat === 'all' ? 'All Categories' : cat.charAt(0).toUpperCase() + cat.slice(1)}
            </option>
          ))}
        </select>

        {/* Export */}
        <button
          onClick={handleExport}
          className="flex items-center gap-2 rounded-xl border border-stone-700 bg-stone-800 px-4 py-2.5 text-sm font-medium text-stone-300 hover:bg-stone-700 transition"
        >
          <Download className="h-4 w-4" />
          Export
        </button>

        {/* Refresh */}
        <button
          onClick={() => window.location.reload()}
          className="flex items-center gap-2 rounded-xl border border-orange-500/30 bg-orange-500/10 px-4 py-2.5 text-sm font-medium text-orange-400 hover:bg-orange-500/20 transition"
        >
          <RefreshCw className="h-4 w-4" />
          Refresh
        </button>
      </div>

      {/* Edit Modal */}
      {isEditing && editEntry && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="w-full max-w-2xl rounded-3xl border border-stone-800 bg-[#141414] p-6 shadow-2xl">
            <h3 className="text-xl font-bold text-white mb-1">Edit Translation</h3>
            <p className="text-sm text-stone-500 mb-6">Key: <code className="text-orange-400">{editEntry.key}</code></p>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-blue-400 mb-2">English</label>
                <textarea
                  value={editEntry.en}
                  onChange={(e) => setEditEntry({ ...editEntry, en: e.target.value })}
                  className="w-full rounded-xl border border-stone-800 bg-stone-900 px-4 py-3 text-sm text-white focus:border-blue-500 focus:outline-none"
                  rows={3}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-emerald-400 mb-2">Indonesian</label>
                <textarea
                  value={editEntry.id}
                  onChange={(e) => setEditEntry({ ...editEntry, id: e.target.value })}
                  className="w-full rounded-xl border border-stone-800 bg-stone-900 px-4 py-3 text-sm text-white focus:border-emerald-500 focus:outline-none"
                  rows={3}
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setIsEditing(false)
                  setEditEntry(null)
                }}
                className="flex-1 rounded-xl border border-stone-700 bg-stone-900 py-3 text-sm font-medium text-stone-300 hover:bg-stone-800 transition"
              >
                Cancel
              </button>
              <button
                onClick={() => handleSave(editEntry)}
                className="flex-1 flex items-center justify-center gap-2 rounded-xl bg-orange-500 py-3 text-sm font-bold text-white hover:bg-orange-600 transition"
              >
                <Save className="h-4 w-4" />
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Translations Table */}
      <SectionCard 
        title={`Translations (${filteredTranslations.length})`} 
        description="Click any row to edit"
      >
        <DataTable
          rows={filteredTranslations}
          columns={[
            { 
              header: 'Key', 
              render: (row) => (
                <div>
                  <span className="text-xs text-stone-500">{row.category}</span>
                  <p className="font-mono text-sm text-white">{row.key}</p>
                </div>
              ),
            },
            { 
              header: 'English', 
              render: (row) => (
                <p className={cn(
                  "text-sm",
                  row.en ? 'text-white' : 'text-rose-400 italic'
                )}>
                  {row.en || '(empty)'}
                </p>
              ),
            },
            { 
              header: 'Indonesian', 
              render: (row) => (
                <p className={cn(
                  "text-sm",
                  row.id ? 'text-white' : 'text-rose-400 italic'
                )}>
                  {row.id || '(empty)'}
                </p>
              ),
            },
            { 
              header: 'Status', 
              render: (row) => {
                const isComplete = row.en && row.id && row.en.trim() && row.id.trim()
                return (
                  <StatusBadge 
                    value={isComplete ? 'complete' : 'incomplete'} 
                  />
                )
              },
            },
          ]}
          onRowClick={(row: TranslationEntry) => {
            setEditEntry(row)
            setIsEditing(true)
          }}
          emptyTitle="No translations found"
          emptyDescription={searchTerm ? 'Try adjusting your search' : 'No translations available'}
        />
      </SectionCard>

      {/* Sync Info */}
      <SectionCard title="Sync Information" description="Mobile app sync status">
        <div className="grid gap-4 md:grid-cols-2">
          <div className="rounded-2xl bg-stone-900/50 border border-stone-800 p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-2 h-2 rounded-full bg-emerald-400" />
              <span className="text-sm font-medium text-white">English (en)</span>
            </div>
            <p className="text-xs text-stone-500">
              Last updated: {enTranslations.data?.updatedAt ? formatDateTime(new Date(enTranslations.data.updatedAt.seconds * 1000).toISOString()) : 'Never'}
            </p>
            <p className="text-xs text-stone-500">
              Version: {enTranslations.data?.version || 'N/A'}
            </p>
          </div>
          
          <div className="rounded-2xl bg-stone-900/50 border border-stone-800 p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-2 h-2 rounded-full bg-emerald-400" />
              <span className="text-sm font-medium text-white">Indonesian (id)</span>
            </div>
            <p className="text-xs text-stone-500">
              Last updated: {idTranslations.data?.updatedAt ? formatDateTime(new Date(idTranslations.data.updatedAt.seconds * 1000).toISOString()) : 'Never'}
            </p>
            <p className="text-xs text-stone-500">
              Version: {idTranslations.data?.version || 'N/A'}
            </p>
          </div>
        </div>
      </SectionCard>
    </div>
  )
}
